<template>
  <div class="ticket-popup" data-testid="ticket-popup" :class="{ 'ticket-popup--rbe': hideCode, 'ticket-popup--open': isMobile && showMobileContent }" v-if="show && (!hideCode || rbeOpen)">
    <div class="title" v-if="!hideCode">
      <div class="title-row">
        <button v-if="isMobile" data-testid="ticket-mobile-toggle" @click="toggleMobileContent" class="mobile-toggle-btn">
          <BzIcon v-if="systemLane" name="receipt_long" />
          <i v-else class="pi pi-receipt"></i>
          {{ showMobileContent ? $t('message.hideTicket') : $t('message.showTicket') }}
        </button>
        <h4 data-testid="ticket-title" :class="{ 'title-long': orderDisplayText.length > 6 }" translate="no">{{ orderDisplayText }}</h4>
        <button v-if="!isMobile || (isMobile && showMobileContent)" data-testid="ticket-mode-toggle" @click="toggleDisplayMode" class="mode-toggle">
          <BzIcon v-if="systemLane" :name="displayMode === 'grouped' ? 'list' : 'grid_view'" />
          <i v-else-if="displayMode === 'grouped'" class="pi pi-list"></i>
          <i v-else class="pi pi-th-large"></i>
          {{ displayMode === 'grouped' ? $t('message.listView') : $t('message.groupView') }}
        </button>
      </div>
    </div>

    <div v-if="contentVisible" class="ticket-content">
      <!-- RBE: the view-mode toggle ("Lista"/"Vista") lives inside the dropdown panel, not the top bar. -->
      <button v-if="hideCode" data-testid="ticket-mode-toggle" @click="toggleDisplayMode" class="mode-toggle mode-toggle--rbe">
        <BzIcon v-if="systemLane" :name="displayMode === 'grouped' ? 'list' : 'grid_view'" />
        <i v-else-if="displayMode === 'grouped'" class="pi pi-list"></i>
        <i v-else class="pi pi-th-large"></i>
        {{ displayMode === 'grouped' ? $t('message.listView') : $t('message.groupView') }}
      </button>
      <div v-if="allergies" class="allergies-warning" data-testid="ticket-allergies">
        <div class="warning-icon">
          <BzIcon v-if="systemLane" name="warning" class="warning-glyph" />
          <p v-else class="emoji" v-html="getWarningEmoji()"></p>
        </div>
        <div class="warning-content">
          <span class="warning-label">{{ $t('message.allergiesLabel') }}</span>
          <p translate="no">{{ allergies }}</p>
        </div>
      </div>
      <div v-if="order.order.comment" class="comments-warning" data-testid="ticket-comment">
        <div class="comment-icon">
          <BzIcon v-if="systemLane" name="chat_bubble" class="warning-glyph" />
          <p v-else class="emoji" v-html="getCommentEmoji()"></p>
        </div>
        <div class="warning-content">
          <span class="warning-label">{{ $t('message.commentsLabel') }}</span>
          <p translate="no">{{ order.order.comment }}</p>
        </div>
      </div>

      <template v-if="loading">
        <div>{{ $t('message.loading') }}</div>
        <div><i class="pi pi-spin pi-spinner" style="font-size: 30px;color: var(--bz-p-blue-500);margin-top:32px;"></i></div>
      </template>

      <template v-else-if="error">
        <div class="error-message">{{ error }}</div>
      </template>

      <template v-else>
        <div class="ticket-table" v-if="orderContent">
          <!-- Classic grouped view -->
          <template v-if="displayMode === 'grouped'">
            <div v-for="(category, categoryName) in orderContent" :key="categoryName" class="ticket-item" data-testid="ticket-item">
              <div class="item-main">
                <p class="emoji" v-html="getFirstIcon(category)"></p>
                <span class="item-count">{{ category.quantity }}</span>
                <span class="group-name" translate="no">{{ categoryName }}</span>
              </div>
              <div class="item-details">
                <div v-for="(item, index) in category.content" :key="index" class="sub-item" translate="no">
                  {{ item.quantity }} x {{ item.display_string }}
                </div>
              </div>
            </div>
          </template>

          <!-- Hierarchical view -->
          <template v-else>
            <order-item 
              v-for="(item, index) in orderContent" 
              :key="index"
              :item="item"
            />
          </template>
        </div>

        <div v-else class="no-content">
          {{ $t('message.contentNotAvailable') }}
        </div>
      </template>

      <div class="buttons-container">
        <button data-testid="ticket-close" @click="$emit('close')">{{ $t('message.close') || 'Close' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import Logger from '@/logger';
import OrderItem from './OrderItem.vue';
import BzIcon from '../ui/components/BzIcon.vue';
import posthog from 'posthog-js';
import { systemLane } from '../ui/lane';

const uEmojiParser = require('universal-emoji-parser')
const logger = new Logger({ screen: 'TicketComponent' });
const STORAGE_KEY = 'preferred_ticket_view_mode';

export default {
  name: 'TicketComponent',
  components: {
    OrderItem,
    BzIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    order: {
      type: Object,
      required: true
    },
    orderDisplayText: {
      type: String,
      required: true
    },
    mode: {
      type: String,
      default: 'grouped',
      validator: function(value) {
        return ['grouped', 'hierarchical'].includes(value)
      }
    },
    hideCode: {
      type: Boolean,
      default: false
    },
    // RBE: content visibility is driven by the toggle in the top bar (CameraScreen), threaded through
    // SelectedOrderInfoComponent, instead of this component's internal showMobileContent.
    rbeOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      orderContent: null,
      loading: false,
      error: null,
      displayMode: localStorage.getItem(STORAGE_KEY) || 'grouped',
      allergies: null,
      isMobile: false,
      showMobileContent: false,
      // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
      systemLane: systemLane()
    };
  },
  computed: {
    contentVisible() {
      // RBE: driven by the bar toggle (rbeOpen). Otherwise keep the original behavior (always shown on
      // desktop, toggled on mobile).
      if (this.hideCode) return this.rbeOpen;
      return !this.isMobile || this.showMobileContent;
    }
  },
  mounted() {
    if (this.order) {
      this.fetchOrderContent();
    }
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    getFirstIcon(category) {
      if (category.content && category.content.length > 0) {
        const iconString = category.content[0].icon || '';
        return uEmojiParser.parse(iconString);
      }
      return uEmojiParser.parse(':package:');
    },
    getWarningEmoji() {
      return uEmojiParser.parse(':warning:');
    },
    getCommentEmoji() {
      return uEmojiParser.parse(':memo:');
    },
    toggleDisplayMode() {
      this.displayMode = this.displayMode === 'grouped' ? 'hierarchical' : 'grouped';
      localStorage.setItem(STORAGE_KEY, this.displayMode);
      // Person property only (no event): enough to segment devices by ticket mode
      // without adding a product event nobody consults.
      posthog.setPersonProperties({'ticket_mode': this.displayMode})
      this.fetchOrderContent();
    },
    async fetchOrderContent() {
      logger.console(`Fetching order content for ID: ${this.order.orderId}`);
      this.loading = true;

      try {
        if (this.displayMode === 'grouped') {
          this.orderContent = this.order.orderContent.grouped;
        } else {
          this.orderContent = this.order.orderContent.hierarchical;
        }
        this.allergies = this.order.order.allergies;
      } catch (error) {
        logger.error('Error fetching order content:', {error: error});
      }
      finally {
        this.loading = false;
      }
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth < 768;
      // Reset mobile content visibility when screen size changes
      if (!this.isMobile) {
        this.showMobileContent = false;
      }
    },
    toggleMobileContent() {
      this.showMobileContent = !this.showMobileContent;
    },
  },
  watch: {
    mode: {
      handler() {
        if (this.order) {
          this.fetchOrderContent();
        }
      }
    }
  }
};
</script>

<style scoped>
.ticket-popup {
  position: fixed;
  top: 64px;
  right: 8px;
  justify-content: center;
  align-items: center;
  background-color: var(--bz-p-slate-900-a80);
  color: var(--bz-p-white);
  padding: 15px;
  border-radius: 15px;
  width: 300px;
  max-height: 75vh;
  display: flex;
  flex-direction: column;
  text-align: center;
}

/* Media query for mobile devices */
@media (max-width: 767px) {
  .ticket-popup {
    right: 0;
    left: 0;
    margin-left: auto;
    margin-right: auto;
  }
  /* RBE pilot: the code lives in the top bar now. Keep only a compact "Ticket" chip (top-right, away
     from the back button) with a TRANSPARENT container so nothing shows when collapsed (no black band).
     The content gets its own panel when expanded. Collapsed by default on mobile. */
  .ticket-popup--rbe {
    top: 116px;
    bottom: auto;
    right: 18px; /* app-standard side margin, matches the top bar / bottom controls */
    left: auto;
    margin: 0;
    /* Adapt to content: comfortable width, never too narrow (the old width:auto collapsed to
       min-content) nor wider than the screen. */
    width: min(86vw, 340px);
    max-width: calc(100% - 24px);
    max-height: 60vh;
    padding: 0;
    background-color: transparent;
  }
  .ticket-popup--rbe .ticket-content {
    background-color: var(--bz-p-slate-900-a96);
    border-radius: 12px;
    border-top: none;
    padding: 14px;
    margin-top: 0;
    box-shadow: 0 10px 30px var(--bz-p-black-a45);
  }
  /* Two classes (.ticket-popup--rbe .mode-toggle--rbe) outrank the single-class base .mode-toggle by
     specificity, so this wins regardless of source order. */
  .ticket-popup--rbe .mode-toggle--rbe {
    position: static;
    display: flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    margin: 0 0 12px auto;
    padding: 7px 12px;
    font-size: 13px;
  }
}

.allergies-warning {
  background: var(--bz-p-yellow-400-a15);
  border: 1px solid var(--bz-p-yellow-400-a20);
  border-radius: 8px;
  margin: 0 0 16px 0;
  padding: 12px;
  display: flex;
  align-items: center; 
  width: 100%;
  box-sizing: border-box;
}

.comments-warning {
  background: var(--bz-p-yellow-400-a15);
  border: 1px solid var(--bz-p-yellow-400-a20);
  border-radius: 8px;
  margin: 0 0 16px 0;
  padding: 12px;
  display: flex;
  align-items: center; 
  width: 100%;
  box-sizing: border-box;
}

.warning-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bz-p-yellow-400);
  margin-right: 12px;
}

.warning-icon i {
  font-size: 20px;
}

.warning-content {
  flex: 1;
  text-align: left;
  min-width: 0;
}

.warning-label {
  display: block;
  color: var(--bz-p-yellow-400);
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.warning-content p {
  color: var(--bz-p-yellow-400);
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  font-style: italic;
}

.error-message {
  color: var(--bz-p-dcdcdc);
  margin: 20px 0;
}

.no-content {
  margin: 20px 0;
  color: var(--bz-p-slate-400);
}

.ticket-popup h4 {
  margin-top: 0;
  font-weight: 500;
  margin-bottom: 8px;
}

.ticket-table {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  max-height: calc(75vh - 140px);
}

.ticket-table::-webkit-scrollbar {
  width: 6px;
}

.ticket-table::-webkit-scrollbar-track {
  background: var(--bz-p-white-a10);
  border-radius: 3px;
}

.ticket-table::-webkit-scrollbar-thumb {
  background: var(--bz-p-white-a30);
  border-radius: 3px;
}

.ticket-table {
  scrollbar-width: thin;
  scrollbar-color: var(--bz-p-white-a30) var(--bz-p-white-a10);
}

.ticket-content{
  width:100%;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--bz-p-white-a10);
  text-align: center;
  width: 100%;
}

.ticket-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.item-main {
  display: flex;
  align-items: center;
  background: var(--bz-p-blue-400-a30);
  padding: 10px;
}

.emoji {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  margin-right: 10px;
}

.group-name {
  flex-grow: 1;
  text-align: left;
  font-weight: 600;
}

.item-details {
  flex-grow: 1;
  text-align: left;
  background: var(--bz-surface--efeff0-a10);
  padding: 10px;
  padding-left: 50px;
  border-top: 1px solid var(--bz-p-white-a10);
}

.item-details .ticket-item {
  margin-bottom: 8px;
}

.item-details .ticket-item:last-child {
  margin-bottom: 0;
}

.sub-item {
  margin: 5px 0;
}

.buttons-container {
  margin-top: 10px;
  display: none;
}

.item-count {
  background: var(--bz-p-white-a20);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  color: var(--bz-p-white);
  margin-right: 8px;
  font-weight: 800;
}

.title {

  width: 100%;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.title h4 {
  margin: 0;
  font-weight: 500;
  font-size: 30px;
  margin-top: 5px;
}

.mobile-toggle-btn {
  position: absolute;
  left: 0;
  background-color: var(--bz-p-blue-400);
  color: var(--bz-p-blue-950);
  border: none;
  cursor: pointer;
  font-weight: 700;
  padding: 8px 12px;
  line-height: 20px;
  border-radius: 999px;
  font-size: 14px;
}

@media (hover: hover) {
  .mobile-toggle-btn:hover {
    background-color: var(--bz-p-blue-500);
    box-shadow: 0 2px 4px var(--bz-p-black-a10);
  }
}

.mode-toggle {
  position: absolute;
  right: 0;
  background-color: var(--bz-p-blue-400);
  color: var(--bz-p-blue-950);
  border: none;
  cursor: pointer;
  font-weight: 700;
  padding: 10px;
  line-height: 20px;
  border-radius: 999px;
}

@media (hover: hover) {
  .mode-toggle:hover {
    background-color: var(--bz-p-blue-500);
    box-shadow: 0 2px 4px var(--bz-p-black-a10);
  }
}

.mode-toggle i {
  font-size: 14px;
}


/* ════ The ticket on the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ════
   What floats over the camera: the raised surface, its edge, radius 16 and the short shadow, on the
   gutter. The order's code at the code's step; the products in one group with hairline dividers (no card
   per product: docs/sistema-diseno/forma-y-tamano.md, the verdict's rule); allergies and comments in the
   warning's container, at 14 and never less. Production's rules above stay for the stores without it. */
:root[data-bz-lane="system"] .ticket-popup {
  top: calc(var(--bz-bar-height) + var(--bz-space-2));
  right: var(--bz-gutter);
  box-sizing: border-box;
  width: 320px;
  padding: var(--bz-space-5);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  font-family: var(--bz-font);
}

/* The code and the toggles share one row, in flow: production put the toggles on top of the row
   (position: absolute), so a long code ran under «Lista» (Adrián, 13-oct-2026, a nine-digit manual
   order). Now the code takes what is left, and if it still does not fit beside the toggle the toggle
   drops to its own line, aligned to the end. */
:root[data-bz-lane="system"] .title-row {
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--bz-space-2) var(--bz-space-3);
}

:root[data-bz-lane="system"] .title h4 {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  text-align: left;
  font-size: var(--bz-text-code);
  line-height: var(--bz-leading-code);
  font-weight: var(--bz-weight-strong);
}

/* A code longer than six characters (a manual order's, a platform's full id) a step down, so it still fits
   beside the toggle on the 320 card. */
:root[data-bz-lane="system"] .title h4.title-long {
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
}

:root[data-bz-lane="system"] .title-row .mode-toggle,
:root[data-bz-lane="system"] .title-row .mobile-toggle-btn {
  position: static;
  flex: none;
}

:root[data-bz-lane="system"] .title-row .mode-toggle {
  margin-left: auto;
}

:root[data-bz-lane="system"] .mode-toggle,
:root[data-bz-lane="system"] .mobile-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-1);
  min-height: var(--bz-target-min);
  padding: 0 var(--bz-space-4);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  box-shadow: none;
  font-family: var(--bz-font);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .mode-toggle .bz-icon,
:root[data-bz-lane="system"] .mobile-toggle-btn .bz-icon {
  font-size: var(--bz-icon-sm);
}

:root[data-bz-lane="system"] .ticket-content {
  margin-top: var(--bz-space-4);
  padding-top: var(--bz-space-4);
  border-top: 1px solid var(--bz-edge);
  text-align: left;
}

:root[data-bz-lane="system"] .no-content,
:root[data-bz-lane="system"] .error-message {
  margin: var(--bz-space-2) 0;
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

:root[data-bz-lane="system"] .ticket-table {
  scrollbar-color: var(--bz-outline) transparent;
  overscroll-behavior: contain;
}

:root[data-bz-lane="system"] .ticket-item {
  margin: 0;
  border-radius: 0;
  border-bottom: 1px solid var(--bz-edge);
}

:root[data-bz-lane="system"] .ticket-item:last-child {
  border-bottom: 0;
}

:root[data-bz-lane="system"] .item-main {
  gap: var(--bz-space-3);
  padding: var(--bz-space-4) 0 var(--bz-space-1);
  background: none;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

:root[data-bz-lane="system"] .emoji {
  margin: 0;
}

/* The product's icon is a Twemoji <img> with no size of its own: Safari drew it at its SVG's default and
   it overflowed its 24 box, cut by the card (Adrián, 14-oct-2026, an iPhone). The box sizes it. */
:root[data-bz-lane="system"] .emoji {
  flex: none;
  width: var(--bz-icon-md);
  height: var(--bz-icon-md);
  overflow: hidden;
}

:root[data-bz-lane="system"] .emoji :deep(img) {
  display: block;
  width: 100%;
  height: 100%;
}

:root[data-bz-lane="system"] .item-count {
  margin: 0;
  padding: 0 var(--bz-space-2);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .group-name {
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .item-details {
  padding: 0 0 var(--bz-space-4) var(--bz-space-9);
  border-top: 0;
  background: none;
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
}

:root[data-bz-lane="system"] .sub-item {
  margin: var(--bz-space-1) 0 0;
}

:root[data-bz-lane="system"] .allergies-warning,
:root[data-bz-lane="system"] .comments-warning {
  align-items: flex-start;
  gap: var(--bz-space-3);
  margin: 0 0 var(--bz-space-2);
  padding: var(--bz-space-3) var(--bz-space-4);
  border: 0;
  border-radius: var(--bz-radius-md);
  background: var(--bz-warn-container);
  color: var(--bz-on-warn-container);
}

/* The icon sits on the label's line, not centred on the whole note: a long comment keeps it at the top. */
:root[data-bz-lane="system"] .warning-icon,
:root[data-bz-lane="system"] .comment-icon {
  display: flex;
  flex: none;
  width: auto;
  height: var(--bz-leading-sm);
  align-items: center;
  margin: 0;
  color: inherit;
  font-size: var(--bz-icon-sm);
}

:root[data-bz-lane="system"] .warning-label {
  margin-bottom: var(--bz-space-1);
  color: inherit;
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .warning-content p {
  color: inherit;
  font-style: normal;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

/* The phone (Adrián, 14-oct-2026): closed, the ticket is a tab hanging from the top of the camera — no gap
   above it, square on top and rounded below, as narrow as the toggle and the code at the 28 step, 56 tall
   (it was a 90-tall card across the screen). Open, it takes the width between the gutters and stops above
   the bar of buttons, and the products scroll inside it. */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .ticket-popup:not(.ticket-popup--rbe) {
    top: var(--bz-bar-height);
    right: 0;
    left: 0;
    width: fit-content;
    max-width: calc(100% - 2 * var(--bz-gutter));
    max-height: calc(100dvh - var(--bz-bar-height) - var(--bz-target-hero) - 3 * var(--bz-gutter));
    margin: 0 auto;
    padding: var(--bz-space-1) var(--bz-space-5) var(--bz-space-1) var(--bz-space-1);
    border-top: 0;
    border-radius: 0 0 var(--bz-radius-lg) var(--bz-radius-lg);
  }

  :root[data-bz-lane="system"] .title h4,
  :root[data-bz-lane="system"] .title h4.title-long {
    font-size: var(--bz-text-3xl);
    line-height: var(--bz-leading-3xl);
  }

  :root[data-bz-lane="system"] .ticket-popup--open:not(.ticket-popup--rbe) {
    width: auto;
    max-width: none;
    margin: 0 var(--bz-gutter);
    padding: var(--bz-space-2) var(--bz-space-4) var(--bz-space-4);
  }

  :root[data-bz-lane="system"] .ticket-popup--open .ticket-content {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    margin-top: var(--bz-space-2);
    padding: var(--bz-space-2) 0 0;
  }

  :root[data-bz-lane="system"] .ticket-popup--open .ticket-table {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
  }

  :root[data-bz-lane="system"] .ticket-popup--rbe {
    left: auto;
    width: min(86vw, 340px);
    padding: 0;
    border: 0;
    background: none;
    box-shadow: none;
  }

  :root[data-bz-lane="system"] .ticket-popup--rbe .ticket-content {
    padding: var(--bz-space-4);
    border: 1px solid var(--bz-edge);
    border-radius: var(--bz-radius-lg);
    background: var(--bz-raised);
    box-shadow: var(--bz-shadow-overlay);
  }
}
</style>